import { useFlyOutProductsBasedOnSelectedCloud } from '../../common/utils/hooks/useFlyOutProductsBasedOnSelectedCloud'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useStyles } from './FlyOutUsageMetering.styles'
import { UsageListingView } from './UsageListingView/UsageListingView'
import { useDispatch, useSelector } from '../../store/hooks'
import { UsageDataState } from '../../modules/usageMetering/types'
import {
  clearUsageMeteringData,
  getUsageData,
} from '../../modules/usageMetering/action'
import { getProducts } from '../../modules/productsListing/action'
import {
  useChatContext,
  useCurrentCloudProvider,
  useAccountId,
  useScreenSize,
  LoadingTypes,
  isLoading,
  searchDropDownOptions,
} from '@labrav/common'
import { searchDropDownOptionMapping } from '../../common/utils/meteringConstants'
import { isEmpty } from 'lodash'
import { CircularProgress, Grid } from '@mui/material'
import { MeteringVideoContainer } from './RightSectionComponents/MeteringVideoContainer'
import { MeteringHelpContainer } from './RightSectionComponents/MeteringHelpContainer'
import { UsageMeteringEmptyState } from './UsageMeteringEmptyState/UsageMeteringEmptyState'
import { UsageMeteringHeaders } from './UsageMeteringFilters/UsageMeteringFilters'
import clsx from 'clsx'
import { CreateMeteringModalOrchestrator } from './CreateMetering/CreateMeteringModalOrchestrator'
import { UsageMeteringNoDataState } from './UsageMeteringNoDataState/UsageMeteringNoDataState'
import { SearchResultsCount } from '@labrav/common'
import { useSyncFlyoutRouteParams } from '../../common/utils/hooks/useSyncFlyoutRouteParams'
import { useSyncCloudMarketplace } from '@labrav/common'
import { CloudMarketplace } from '../../modules/productsListing/types'
import { ListingCreationPage } from '../../common/components/ListingCreationPage/ListingCreationPage'
import type { AccountDataSelection } from '@labrav/common'

export const FlyOutUsageMetering: React.FC = () => {
  useSyncFlyoutRouteParams()
  useSyncCloudMarketplace()

  const { classes } = useStyles()
  const dispatch = useDispatch()
  const { cloud: routeCloud, accountId: routeAccountId } = useParams<{
    cloud?: string
    accountId?: string
  }>()
  const { data, metaData, filters } = useSelector<UsageDataState>(
    state => state.usageData
  )
  const { dateRangeFilter, status, cloud } = filters
  const { pageSize: pageSizeValue } = useScreenSize()
  const { products } = useFlyOutProductsBasedOnSelectedCloud()
  const accountIdFromHook = useAccountId({ product: 'flyout' }) || ''
  const accountId = routeAccountId || accountIdFromHook || ''
  const selectedCloudTypeProductListing = useCurrentCloudProvider()

  const cloudMarketplace: CloudMarketplace = routeCloud
    ? (routeCloud.toUpperCase() as CloudMarketplace)
    : selectedCloudTypeProductListing

  const allAccounts = useSelector<AccountDataSelection[]>(
    state =>
      (state.accountSelection?.availableAccounts as AccountDataSelection[]) ||
      []
  )

  useEffect(() => {
    if (routeCloud && routeCloud.toUpperCase() && routeAccountId) {
      const flyoutAccounts = allAccounts.filter(
        acc => acc.product?.toLowerCase() === 'flyout'
      )

      const selectedCloud = routeCloud?.toUpperCase() as CloudMarketplace

      let accountIdForCloud = flyoutAccounts.find(
        acc => acc.id === routeAccountId && acc.cloud === selectedCloud
      )?.id

      if (!accountIdForCloud) {
        accountIdForCloud = flyoutAccounts.find(
          acc => acc.cloud === selectedCloud
        )?.id
      }
    }
  }, [routeCloud, routeAccountId, allAccounts])

  const [pageNumber, setPageNumber] = useState(1)
  const [isUsageLoading, setIsUsageLoading] = useState(false)
  const [showMeteringModal, setShowMeteringModal] = useState(false)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false)
  const updateMeteringModalVisibility = () =>
    setShowMeteringModal(!showMeteringModal)
  const loading = useSelector<boolean>(state =>
    isLoading(state.loading)(LoadingTypes.GENERAL)
  )
  const resetPagenumber = () => {
    setPageNumber(1)
  }
  const totalCount = metaData?.totalCount || 0
  const lastItemRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isUsageLoading || !node) return
      const observer = new IntersectionObserver(entries => {
        if (
          entries[0].isIntersecting &&
          data.length < totalCount &&
          totalCount > pageSizeValue
        ) {
          setPageNumber(prev => prev + 1)
        }
      })
      observer.observe(node)
      return () => observer.disconnect()
    },
    [isUsageLoading, data.length, totalCount, pageSizeValue]
  )
  const [searchNow, setSearchNowState] = useState(false)
  const prevSearchNowRef = useRef(searchNow)
  const skipFetchFromSearchEffectRef = useRef(false)
  const [showCrossMark, setShowCrossMark] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [selectedOption, setSelectedOption] = useState(
    searchDropDownOptions.meteringOptions[0]
  )
  useChatContext('usageMetering', () => ({
    cloud: cloudMarketplace,
    status,
    dateRange: dateRangeFilter,
    search: inputValue,
    searchField: selectedOption,
    visible: { count: totalCount, page: pageNumber },
  }))

  const setSearchNow = useCallback((value: React.SetStateAction<boolean>) => {
    setSearchNowState(prev => {
      const next = typeof value === 'function' ? value(prev) : value
      if (!next) {
        skipFetchFromSearchEffectRef.current = false
      }
      return next
    })
  }, [])
  const lastFetchedRef = useRef<{
    accountId: string
    cloudMarketplace: CloudMarketplace
  } | null>(null)

  useEffect(() => {
    if (accountId && cloudMarketplace) {
      const lastFetched = lastFetchedRef.current
      const shouldFetch =
        isEmpty(products) &&
        (!lastFetched ||
          lastFetched.accountId !== accountId ||
          lastFetched.cloudMarketplace !== cloudMarketplace)

      if (shouldFetch) {
        lastFetchedRef.current = { accountId, cloudMarketplace }
        dispatch(getProducts(accountId, cloudMarketplace))
      }
    }
  }, [accountId, products, cloudMarketplace, dispatch])

  useEffect(() => {
    if (accountId && cloudMarketplace) {
      lastFetchedRef.current = null
      dispatch(clearUsageMeteringData())
    }
  }, [cloudMarketplace, accountId, dispatch])

  useEffect(() => {
    if (!accountId || !cloudMarketplace) return

    const lastFetched = lastFetchedRef.current
    const shouldFetchProducts =
      isEmpty(products) &&
      (!lastFetched ||
        lastFetched.accountId !== accountId ||
        lastFetched.cloudMarketplace !== cloudMarketplace)

    if (shouldFetchProducts) {
      lastFetchedRef.current = { accountId, cloudMarketplace }
      dispatch(getProducts(accountId, cloudMarketplace))
    }
  }, [accountId, cloudMarketplace, products, dispatch])

  const areFiltersApplied = (): boolean => {
    const hasDateFilter = Boolean(
      dateRangeFilter?.startDate || dateRangeFilter?.endDate
    )
    const hasStatusFilter = status.length > 0
    const hasCloudFilter = cloud.length > 0
    return hasDateFilter || hasStatusFilter || hasCloudFilter
  }

  useEffect(() => {
    if (prevSearchNowRef.current === true && searchNow === false) {
      resetPagenumber()
      dispatch(clearUsageMeteringData())
    }
    prevSearchNowRef.current = searchNow

    if (skipFetchFromSearchEffectRef.current) {
      skipFetchFromSearchEffectRef.current = false
      return
    }

    if (pageNumber && accountId) {
      setIsUsageLoading(true)
      dispatch(
        getUsageData({
          cloud: [cloudMarketplace],
          pageSize: pageSizeValue,
          pageNumber,
          ...(searchNow && !isEmpty(inputValue) && !isEmpty(selectedOption)
            ? {
                input: inputValue,
                selectedInput:
                  searchDropDownOptionMapping.meteringOptions[
                    selectedOption as keyof typeof searchDropDownOptionMapping.meteringOptions
                  ],
                sectionName: 'usage-metering',
              }
            : {}),
          accountId,
          cloudType: cloudMarketplace,
        })
      )
        .then(() => {
          setHasAppliedFilters(areFiltersApplied())
        })
        .finally(() => {
          setIsUsageLoading(false)
          setIsInitialLoad(false)
        })
    }
  }, [
    pageNumber,
    filters,
    searchNow,
    accountId,
    cloudMarketplace,
    dispatch,
    pageSizeValue,
  ])

  const shouldShowInitialEmptyState =
    isEmpty(data) &&
    !loading &&
    !isUsageLoading &&
    (isInitialLoad || !hasAppliedFilters)

  const handleSearchClick = () => {
    if (inputValue.trim() !== '' && accountId) {
      if (!searchNow) {
        skipFetchFromSearchEffectRef.current = true
      }
      dispatch(clearUsageMeteringData())
      resetPagenumber()
      setSearchNow(true)
      setShowCrossMark(true)
      setIsUsageLoading(true)
      dispatch(
        getUsageData({
          cloud: [cloudMarketplace],
          pageSize: pageSizeValue,
          pageNumber: 1,
          input: inputValue,
          selectedInput:
            searchDropDownOptionMapping.meteringOptions[
              selectedOption as keyof typeof searchDropDownOptionMapping.meteringOptions
            ],
          sectionName: 'usage-metering',
          accountId,
          cloudType: cloudMarketplace,
        })
      )
        .then(() => {
          setHasAppliedFilters(areFiltersApplied())
        })
        .finally(() => {
          setIsUsageLoading(false)
          setIsInitialLoad(false)
        })
    }
  }
  const shouldShowListingCreationPage =
    isEmpty(products) && accountId && !loading

  return (
    <>
      {shouldShowListingCreationPage ? (
        <ListingCreationPage />
      ) : shouldShowInitialEmptyState ? (
        <div>
          <UsageMeteringEmptyState
            handleClick={updateMeteringModalVisibility}
          />
        </div>
      ) : (
        <div>
          <UsageMeteringHeaders
            updateMeteringModalVisibility={updateMeteringModalVisibility}
            resetPagenumber={resetPagenumber}
            handleSearchClick={handleSearchClick}
            searchNow={searchNow}
            setSearchNow={setSearchNow}
            showCrossMark={showCrossMark}
            setShowCrossMark={setShowCrossMark}
            inputValue={inputValue}
            setInputValue={setInputValue}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            count={totalCount}
          />
          <div className={classes.container}>
            <div className={classes.formWidth} data-testid="left-section">
              {searchNow && !isUsageLoading && (
                <div className={classes.contentWrapper}>
                  <SearchResultsCount
                    totalCount={totalCount}
                    loading={isUsageLoading}
                  />
                </div>
              )}
              <div
                className={clsx(classes.listingWrapper, classes.bottomMargin)}
              >
                {!!data?.length && (
                  <Grid
                    container
                    size={{
                      sm: 12,
                      md: 12,
                    }}
                    sx={{
                      gap: 2,
                    }}
                  >
                    {data.map((item, index) => {
                      const isLastItem = index === data.length - 1
                      return (
                        <div
                          key={item.labraMeteringId}
                          ref={isLastItem ? lastItemRef : null}
                          style={{ width: '100%' }}
                        >
                          <UsageListingView usageData={item} />
                        </div>
                      )
                    })}
                  </Grid>
                )}
                {isUsageLoading && (
                  <CircularProgress className={classes.loadingSpinner} />
                )}
                {!isUsageLoading &&
                  isEmpty(data) &&
                  hasAppliedFilters &&
                  !searchNow && (
                    <UsageMeteringNoDataState
                      text={'Create metering to view list here'}
                    />
                  )}
              </div>
            </div>
            <div
              className={clsx(classes.activityLogWidth)}
              data-testid="right-section"
            >
              <div className={classes.stickyRightSection}>
                <MeteringVideoContainer />
                <MeteringHelpContainer />
              </div>
            </div>
          </div>
        </div>
      )}
      {showMeteringModal && (
        <CreateMeteringModalOrchestrator
          updateMeteringModalVisibility={updateMeteringModalVisibility}
        />
      )}
    </>
  )
}
